import * as THREE from 'three';
import { WebGLElement, findThreeParent } from './base.js';

// <three-image src="/me.png" position="-2.7 0 0" height="6" opacity="0.85"> — a textured,
// camera-facing plane for a transparent PNG. Sized to the image's aspect; `height` in world
// units. Parents into the nearest <three-scene>.
//   Default: unlit (shows the photo as-is).
//   `hologram` attr: holographic shader — recolors the figure into `color` (default cyan)
//   by luminance, animated scanlines + vertex glitch + flicker, additive glow. Respects the
//   PNG alpha (only the figure holograms). Ref: threejs-journey hologram lesson.
//   `anchor="left|right|center"`: pin the figure to the visible edge of the viewport at its
//   z-plane instead of a fixed x — stays fully in frame at any page width (same camera-edge
//   math as rapier-physics). Tracks resize via ResizeObserver. `inset` = world-unit gap from
//   the edge. y/z still come from `position`; x is computed.

const HOLO_VERT = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  float rand(vec2 v) { return fract(sin(dot(v, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const HOLO_FRAG = /* glsl */ `
  uniform float uTime;
  uniform sampler2D uTex;
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec2 vUv;
  float rand(vec2 v) { return fract(sin(dot(v, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    // TV glitch: occasional FAST bursts (mostly clean, then a brief intense tear)
    float win = floor(uTime * 0.5);                 // 2s windows
    float burst = step(0.55, rand(vec2(win, 7.7))); // ~45% of windows glitch...
    burst *= step(fract(uTime * 0.5), 0.16);        // ...only the first ~0.3s (brief)
    float t = floor(uTime * 36.0);                  // fast ticks during the burst
    float band = floor(vUv.y * 16.0);
    float trig = step(0.55, rand(vec2(band, t))) * burst;
    float shift = (rand(vec2(band, t + 0.5)) - 0.5) * 0.18 * trig;
    vec2 guv = vec2(vUv.x + shift, vUv.y);
    float split = 0.01 * trig;
    float r = texture2D(uTex, guv + vec2(split, 0.0)).r;
    float g = texture2D(uTex, guv).g;
    float b = texture2D(uTex, guv - vec2(split, 0.0)).b;
    float a = texture2D(uTex, guv).a;
    if (a < 0.02) discard;
    vec3 src = vec3(r, g, b);
    float scan = pow(sin((vUv.y * 90.0) - uTime * 3.0) * 0.5 + 0.5, 2.0);
    float lum = dot(src, vec3(0.299, 0.587, 0.114));
    vec3 holo = uColor * (0.4 + lum * 1.1) + uColor * scan * 0.1;
    vec3 col = mix(src, holo, 0.55) + uColor * trig * 0.3; // glitched bands flash
    // pulse (kept)
    float fl = sin(uTime * 9.0) * 0.5 + sin(uTime * 27.0) * 0.25 + sin(uTime * 3.3) * 0.25;
    float pulse = 0.75 + 0.25 * (fl * 0.5 + 0.5);
    float alpha = a * uOpacity * (0.95 + scan * 0.05) * pulse;
    gl_FragColor = vec4(col, alpha);
  }
`;

export class ThreeImage extends WebGLElement {
  static properties = {
    ...WebGLElement.properties,
    src: { type: String },
    height: { type: Number },
    opacity: { type: Number },
    color: { type: String },
    anchor: { type: String },
    inset: { type: Number },
  };

  constructor() {
    super();
    this.src = '';
    this.height = 4;
    this.opacity = 1;
    this.color = '#7dcfff';
    this.anchor = '';
    this.inset = 0;
    this._holoMat = null;
    this._cb = null;
    this._scene = null;
    this._meshWidth = 0;
    this._ro = null;
  }

  createObject3D() {
    const h = this.height || 4;
    const holo = this.hasAttribute('hologram');
    let mat;
    if (holo) {
      mat = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uTex: { value: null },
          uColor: { value: new THREE.Color(this.color || '#7dcfff') },
          uOpacity: { value: this.opacity },
        },
        vertexShader: HOLO_VERT,
        fragmentShader: HOLO_FRAG,
      });
      this._holoMat = mat;
    } else {
      mat = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: this.opacity,
        depthWrite: false,
        toneMapped: false,
        side: THREE.DoubleSide,
      });
    }
    this._meshWidth = h;
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(h, h), mat);
    if (this.src) {
      new THREE.TextureLoader().load(this.src, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = 8;
        if (this._holoMat) this._holoMat.uniforms.uTex.value = tex;
        else {
          mat.map = tex;
          mat.needsUpdate = true;
        }
        const img = tex.image;
        if (img?.width && img?.height) {
          mesh.geometry.dispose();
          this._meshWidth = h * (img.width / img.height);
          mesh.geometry = new THREE.PlaneGeometry(this._meshWidth, h);
          this._applyAnchor(); // real width known → re-pin to the edge
        }
      });
    }
    return mesh;
  }

  async connectedCallback() {
    super.connectedCallback();
    const scene = findThreeParent(this);
    this._scene = scene;
    // holographic shader: drive its time uniform from the scene frame loop
    if (this._holoMat && scene?.addFrameCallback) {
      this._cb = (dt) => {
        if (this._holoMat) this._holoMat.uniforms.uTime.value += dt || 0.016;
      };
      scene.addFrameCallback(this._cb);
    }
    // responsive edge-anchor: pin x to the visible edge, re-pin on resize
    if (this.anchor && scene) {
      if (scene.whenReady?.then) await scene.whenReady;
      if (!this.isConnected) return;
      this._applyAnchor();
      this._ro = new ResizeObserver(() => this._applyAnchor());
      this._ro.observe(scene);
    }
  }

  updated(changed) {
    super.updated(changed);
    if (this.anchor && (changed.has('position') || changed.has('anchor') || changed.has('inset'))) {
      this._applyAnchor();
    }
  }

  // Pin the figure's x to the camera's visible left/right edge at the mesh's z-plane, so it
  // stays fully in frame at any page width. Visible half-width from fov + live canvas aspect
  // (same math as rapier-physics._sizeFromCount). y/z keep their `position` values.
  _applyAnchor() {
    const scene = this._scene;
    const mesh = this.object3D;
    if (!this.anchor || !scene || !mesh) return;
    const cam = scene.getActiveCamera?.() || scene._defaultCamera;
    const renderer = scene.getRenderer?.();
    if (!cam || !renderer || !this._meshWidth) return;
    const size = renderer.getSize(new THREE.Vector2());
    const aspect = size.x / Math.max(1, size.y);
    const dist = Math.abs((cam.position?.z ?? 0) - mesh.position.z);
    const visHalfW = Math.tan(((cam.fov || 60) * Math.PI) / 360) * dist * aspect;
    const halfFig = this._meshWidth / 2;
    const inset = this.inset || 0;
    if (this.anchor === 'left') mesh.position.x = -visHalfW + halfFig + inset;
    else if (this.anchor === 'right') mesh.position.x = visHalfW - halfFig - inset;
    else if (this.anchor === 'center') mesh.position.x = 0;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._cb) this._scene?.removeFrameCallback?.(this._cb);
    this._ro?.disconnect();
  }
}

customElements.define('three-image', ThreeImage);
